/**
 * Archivo: ComunicadosOficiales.js
 * Autor: Acher Egido Mairal
 * Fecha de última modificación: 2024-07-10
 * 
 * Descripción:
 * Este componente define la vista de los comunicados oficiales, permitiendo la visualización, adición, modificación
 * y eliminación de comunicados. También permite descargar archivos PDF asociados a los comunicados.
 * 
 * Dependencias:
 * - react: ^18.2.0
 * - js-cookie: ^3.0.1
 * - Servicios/ComunicadosServicios: Funciones para interactuar con el servicio de comunicados.
 * - Componentes/MetaTags: Componente para las etiquetas meta del documento.
 * - Componentes/Cabecera: Componente para la cabecera principal.
 * - Componentes/SegundaCabecera: Componente para la segunda cabecera de navegación.
 * - Componentes/VentanaAñadirComunicado: Componente modal para añadir un nuevo comunicado.
 * - Componentes/VentanaModificarComunicado: Componente modal para modificar un comunicado existente.
 * 
 * Propósito:
 * Proveer una interfaz para gestionar los comunicados oficiales, permitiendo su visualización, adición, modificación
 * y eliminación, así como la descarga de archivos PDF asociados a cada comunicado.
 */

import { default as React, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import '../Estilos/estilos.css';
import MetaTags from '../Componentes/MetaTags';
import Cabecera from '../Componentes/Cabecera';
import SegundaCabecera from '../Componentes/SegundaCabecera';
import VentanaAñadirComunicado from '../Componentes/VentanaAñadirComunicado';
import VentanaModificarComunicado from '../Componentes/VentanaModificarComunicado';
import { listaComunicados, descargarComunicadoPDF } from '../Servicios/ComunicadosServicios';

import pdfIcon from '../Imagenes/Iconos/Pdf.png';
import lapizNegro from '../Imagenes/Iconos/LapizNegro.png';

function ComunicadosOficiales() {
  const [comunicados, setComunicados] = useState([]);
  const [token, setToken] = useState('');
  const [permisos, setPermisos] = useState(null);
  const [hayVentanas, setHayVentanas] = useState(false);
  const [mostrarAñadirComunicado, setMostrarAñadirComunicado] = useState(false);
  const [mostrarModificarComunicado, setMostrarModificarComunicado] = useState(false);
  const [comunicadoSeleccionado, setComunicadoSeleccionado] = useState(null);

  const handleAñadirComunicado = () => {
    setMostrarAñadirComunicado(true);
    setHayVentanas(true);
  };

  const closeAñadirComunicado = () => {
    setMostrarAñadirComunicado(false);
    setHayVentanas(false);
  };

  const handleModificarComunicado = (comunicado) => {
    setComunicadoSeleccionado(comunicado);
    setMostrarModificarComunicado(true);
    setHayVentanas(true);
  };

  const closeModificarComunicado = () => {
    setMostrarModificarComunicado(false);
    setHayVentanas(false);
  };

  useEffect(() => {
    const obtenerDatos = async () => {
      const tokenNuevo = Cookies.get('token');
      const permisosNuevos = Cookies.get('permisos');
      setToken(tokenNuevo);
      setPermisos(permisosNuevos);
      await actualizarListaComunicados(tokenNuevo);
    };
    obtenerDatos();
  }, [token]);

  const actualizarListaComunicados = async (token) => {
    const resultado = await listaComunicados(token);
    if (resultado.success) {
      setComunicados(resultado.data);
      //console.log("Comunicados:", comunicados)
    } else {
      console.log(resultado ? resultado.message : 'Error desconocido');
    }
  };

  const handleDescargarComunicadoPDF = (id_comunicado) => {
    descargarComunicadoPDF(token, id_comunicado)
      .then(response => {
        if (response.success) {
          const link = document.createElement('a');
          link.href = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
          link.download = `comunicado_${id_comunicado}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          console.log(response.message);
        }
      })
      .catch(() => {
        console.log('Error al descargar PDF');
      });
  };

  const renderComunicados = (comunicados) => {
    if (!comunicados) {
      return (
        <div className='row-fluid p-2 d-flex justify-content-between'>
          <div>No hay ningún Comunicado oficial.</div>
        </div>
      )
    }

    return comunicados.map((comunicado, index) => (
      <div key={index} className="row mb-2 align-items-center" style={{ backgroundColor: '#E9ECEF' }}>
        <div className="col-xl-8 col-lg-8 col-md-8 col-5 py-2">
          <span className="text-black">{comunicado.titulo_comunicado}</span>
        </div>
        <div className="col-xl-2 col-lg-2 col-md-2 col-4 d-flex justify-content-center">
          <span className="text-black">{new Date(comunicado.fecha_comunicado).toLocaleDateString()}</span>
        </div>
        <div className="col-1 py-2 d-flex justify-content-end">
          {comunicado.pdf_comunicado && <img
            alt="descargar"
            src={pdfIcon}
            style={{ height: '2rem', width: '2rem', cursor: 'pointer' }}
            onClick={() => handleDescargarComunicadoPDF(comunicado.id_comunicado)}
          />}
        </div>
        <div className="col-1 d-flex justify-content-end">
          {permisos === "Administrador" &&
            <img
              alt="modificar"
              src={lapizNegro}
              style={{ height: '2rem', width: '2rem', cursor: 'pointer' }}
              onClick={() => handleModificarComunicado(comunicado)}
            />
          }
        </div>
      </div>
    ));
  };

  return (
    <>
      <MetaTags />
      <div className="fondo">
        <Cabecera permisos={permisos}/>
        <div className='d-none d-md-block '>
          <SegundaCabecera hayVentanas={hayVentanas} permisos={permisos} />
          {mostrarAñadirComunicado && <VentanaAñadirComunicado
            onClose={closeAñadirComunicado}
            actualizarLista={async () => {
              await actualizarListaComunicados(Cookies.get('token'));
            }}
          />}
          {mostrarModificarComunicado && <VentanaModificarComunicado
            onClose={closeModificarComunicado}
            actualizarLista={async () => {
              await actualizarListaComunicados(Cookies.get('token'));
            }}
            comunicado={comunicadoSeleccionado}
          />}
          <div className='container-fluid d-flex p-2'>
            <div className='container-fluid col-11' style={{ opacity: hayVentanas ? 0.4 : 1 }}>
              <div className='row-fluid d-flex justify-content-between p-2'>
                <h3 className="text-black">Comunicados oficiales</h3>
                {permisos === "Administrador" &&
                  <button className="btn" style={{ backgroundColor: '#3C3C3C', border: '1px solid black' }} type="submit" onClick={() => handleAñadirComunicado()}>
                    <span className='text-white'>Añadir comunicado</span>
                  </button>
                }
              </div>
              {comunicados && <div className="row mb-1">
                  <div className="col-8"><b>Título</b></div>
                  <div className="col-2 d-flex justify-content-center "><b>Fecha de publicación</b></div>
                  <div className="col-2"></div>
                </div>}
              <div className='container-fluid ' style={{ height: 'calc(100vh - 16rem)', overflowY: 'auto' }}>
                {renderComunicados(comunicados)}
              </div>
            </div>
          </div>
        </div>
        <div className='d-block d-md-none'>
          <div className='container-fluid d-flex p-0'>
            <div className='container-fluid col-11 p-0' style={{ opacity: hayVentanas ? 0.4 : 1 }}>
              <div className='row-fluid d-flex justify-content-between p-1'>
                <h3 className="text-black">Comunicados oficiales curso 2023/2024</h3>
              </div>
              {comunicados && <div className="row mb-1">
                  <div className="col-5 ml-2"><b>Título</b></div>
                  <div className="col-6 d-flex justify-content-center "><b>Fecha de publicación</b></div>
                </div>}
              <div className='container-fluid ' style={{ height: 'calc(100vh - 12rem)', overflowY: 'auto' }}>
                {renderComunicados(comunicados)}
              </div>
            </div>
          </div>
        </div>
        {/** Pantalla de overlay para que no se pueda seleccionar ningún objeto o interactuar si hay alguna ventana */}
        {hayVentanas && (
          <div className="overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 2
          }}></div>
        )}
      </div>
    </>
  );
}

export default ComunicadosOficiales;
